import React from 'react';
import { motion } from 'framer-motion';
import { Clock, Video, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function UpcomingClasses({ course }) {
  const today = new Date();
  const classes = course?.upcoming_classes || [];

  if (classes.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Upcoming Classes</h2>
          <p className="text-muted-foreground text-sm mt-1">Stay on schedule with your live sessions</p>
        </div>
        <div className="glass rounded-2xl border border-white/80 p-8 text-center">
          <p className="text-muted-foreground text-sm">No upcoming classes scheduled yet.</p>
        </div>
      </div>
    );
  }

  const formatTime = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':');
    const hour = parseInt(h, 10);
    const period = hour >= 12 ? 'PM' : 'AM';
    const h12 = ((hour + 11) % 12) + 1;
    return `${h12}:${m} ${period}`;
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-bold tracking-tight">Upcoming Classes</h2>
        <p className="text-muted-foreground text-sm mt-1">Stay on schedule with your live sessions</p>
      </div>

      <div className="grid gap-4">
        {classes.map((cls, i) => {
          const classDate = new Date(cls.date);
          const daysLeft = Math.ceil((classDate - today) / (1000 * 60 * 60 * 24));
          const isToday = daysLeft === 0;
          const isSoon = daysLeft <= 2 && daysLeft > 0;

          return (
            <motion.div
              key={cls.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              whileHover={{ y: -2, transition: { duration: 0.2 } }}
              className={`glass rounded-2xl border p-5 flex flex-col sm:flex-row gap-4 items-start sm:items-center ${
                isToday ? 'border-primary/50 bg-primary/5' : 'border-white/80'
              }`}
            >
              <div className={`flex-shrink-0 w-16 rounded-2xl text-center py-3 ${isToday ? 'bg-primary' : 'bg-secondary/10 border border-black/10'}`}>
                <p className={`text-[10px] font-bold uppercase tracking-wider ${isToday ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                  {classDate.toLocaleString('default', { month: 'short' })}
                </p>
                <p className={`font-heading font-black text-2xl leading-none ${isToday ? 'text-primary-foreground' : 'text-foreground'}`}>
                  {classDate.getDate()}
                </p>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h3 className="font-heading font-bold text-base">{cls.title}</h3>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isToday ? 'bg-primary text-primary-foreground' : isSoon ? 'bg-orange-100 text-orange-600' : 'bg-secondary/15 text-muted-foreground'}`}>
                    {isToday ? 'TODAY' : `In ${daysLeft} days`}
                  </span>
                  {cls.status && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-600">{cls.status}</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-3 h-3 text-primary" />
                    {formatTime(cls.time)}{cls.duration ? ` · ${cls.duration}` : ''}
                  </span>
                </div>
                {cls.instructor_name && (
                  <p className="text-xs text-muted-foreground mt-1">by {cls.instructor_name}</p>
                )}
              </div>

              <Button
                size="sm"
                asChild={!!cls.meeting_link}
                className={`gap-2 rounded-xl font-semibold flex-shrink-0 ${isToday ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/30' : ''}`}
                variant={isToday ? 'default' : 'outline'}
              >
                {cls.meeting_link ? (
                  <a href={cls.meeting_link} target="_blank" rel="noopener noreferrer">
                    <Video className="w-4 h-4" />
                    {isToday ? 'Join Now' : 'View'}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                ) : (
                  <>
                    <Video className="w-4 h-4" />
                    {isToday ? 'Join Now' : 'View'}
                    <ExternalLink className="w-3 h-3" />
                  </>
                )}
              </Button>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
